import type { BqJobResponseApi } from './bqJob.ts';
import type { ConstValues } from './helpers.ts';

export type DealInsightsFilterEntryApi = {
  name: string;
  impressions: number;
};

export type DealInsightsFilterNullableEntryApi = {
  name: string | null;
  impressions: number;
};

export type DealInsightsDealEntryApi = DealInsightsFilterEntryApi & {
  deal_id: string;
  ox_deal_id: number;
};

export type DealInsightsFilterOptionsApi = {
  deal: DealInsightsDealEntryApi[];
  buyer_name: DealInsightsFilterNullableEntryApi[];
  dsp_name: DealInsightsFilterEntryApi[];
  pub_name: DealInsightsFilterEntryApi[];
  type_name: DealInsightsFilterEntryApi[];
  domain_bundle: DealInsightsFilterEntryApi[];
  media_subtype: DealInsightsFilterEntryApi[];
  channel: DealInsightsFilterEntryApi[];
  adsize: DealInsightsFilterEntryApi[];
  ad_duration: DealInsightsFilterEntryApi[];
};

export type DealInsightsFilterOptionsParamsApi = {
  start_date: string;
  end_date: string;
  account: string;
  ox_deal_id: number[];
  buyer_name: (string | null)[];
  dsp_name: string[];
  pub_name: string[];
  type_name: string[];
  domain_bundle: string[];
  media_subtype: string[];
  channel: string[];
  adsize: string[];
  ad_duration: string[];
};

export type DealInsightsTableInputApi = {
  limit: number;
  offset: number;
  sort_field: string;
  sort_order: 'asc' | 'desc';
};

export type DateResolutionInput = {
  include: boolean;
  date_resolution: 'daily' | 'weekly' | 'monthly';
};

export type DealInsightsChartInputApi = {
  general_stats?: boolean;
  spend?: DateResolutionInput;
  bidrate_winrate?: DateResolutionInput;
  inventory_distributions?: boolean;
  delivery_by_domain_bundle?: DealInsightsTableInputApi;
  delivery_by_ad_size?: DealInsightsTableInputApi;
  delivery_by_ad_duration?: boolean;
  audiences?: DealInsightsTableInputApi;
  deal_stats?: DealInsightsTableInputApi;
  geolocation?: boolean;
};

export type DealInsightsCreateJobIdParamsApi = DealInsightsFilterOptionsParamsApi & {
  charts: DealInsightsChartInputApi;
};

export type DealInsightsCreateJobIdResponseApi = {
  job_id: string;
  params: DealInsightsCreateJobIdParamsApi;
};

export type DealInsightsGeneralStatsOldApi = {
  impressions: number | null;
  spend: number | null;
  ecpm: number | null;
  bid_rate: number | null;
  win_rate: number | null;
  viewability: number | null;
  vcr: number | null;
  ctr: number | null;
};

export type DealInsightsGeneralStatsEntryApi = {
  value: number | null;
  percentage_change: number | null;
};

export type DealInsightsGeneralStatsApi = {
  impressions: DealInsightsGeneralStatsEntryApi;
  spend: DealInsightsGeneralStatsEntryApi;
  ecpm: DealInsightsGeneralStatsEntryApi;
  bid_rate: DealInsightsGeneralStatsEntryApi;
  win_rate: DealInsightsGeneralStatsEntryApi;
  viewability: DealInsightsGeneralStatsEntryApi;
  vcr: DealInsightsGeneralStatsEntryApi;
  ctr: DealInsightsGeneralStatsEntryApi;
  iqs_on_impression: DealInsightsGeneralStatsEntryApi;
  previous_period_calculated: boolean;
};

export type DealInsightsSpendApi = {
  date: string;
  spend: number;
  ecpm: number;
  impressions: number | null;
  viewability: number | null;
  vcr: number | null;
};

export type DealInsightsBidRateWinRateApi = {
  bid_rate: number;
  date: string;
  win_rate: number;
  impressions: number | null;
  viewability: number | null;
  vcr: number | null;
};

export type DealInsightsRangeApi = {
  minimum: number | null;
  maximum: number | null;
};

export type DealInsightsStatsObjectApi = {
  deal_name: string;
  deal_id: string;
  ox_deal_id: number;
  health_score: number | null;
  iqs_on_impression: number | null;
  impressions: number | null;
  spend: number | null;
  vcr: number | null;
  ctr: number | null;
  ecpm: number | null;
  bid_rate: number | null;
  win_rate: number | null;
  viewability: number | null;
};

export type DealInsightsDealStatsApi = {
  total_rows: number;
  impressions_range: DealInsightsRangeApi;
  spend_range: DealInsightsRangeApi;
  ctr_range: DealInsightsRangeApi;
  ecpm_range: DealInsightsRangeApi;
  objects: DealInsightsStatsObjectApi[];
};

export type DealInsightsDeliveryByDomainBundleObjectApi = {
  domain_bundle: string;
  app_name: string;
  clicks: number;
  ctr: number;
  ecpm: number;
  sov: number;
  spend: number;
  vcr: number;
  viewability: number | null;
  impressions: number;
};

export type DealInsightsDeliveryByDomainBundleApi = {
  total_rows: number;
  impressions_range: DealInsightsRangeApi;
  clicks_range: DealInsightsRangeApi;
  ctr_range: DealInsightsRangeApi;
  ecpm_range: DealInsightsRangeApi;
  sov_range: DealInsightsRangeApi;
  spend_range: DealInsightsRangeApi;
  vcr_range: DealInsightsRangeApi;
  viewability_range: DealInsightsRangeApi;
  objects: DealInsightsDeliveryByDomainBundleObjectApi[];
};

export type DealInsightsAudiencesApi = {
  total_rows: number;
  objects: DealInsightsAudiencesObjectApi[];
  avg_frequency_range: DealInsightsRangeApi;
  impressions_range: DealInsightsRangeApi;
  uniques_range: DealInsightsRangeApi;
  viewability_range: DealInsightsRangeApi;
  spend_range: DealInsightsRangeApi;
};

export type DealInsightsAudiencesObjectApi = {
  avg_frequency: number;
  deal_id: string;
  deal_name: string;
  ox_deal_id: number;
  impressions: number;
  uniques: number;
  viewability: number | null;
  spend: number;
};

export type DealInsightsDeliveryByAdSizeObjectApi = {
  ad_size: string;
  clicks: number;
  ctr: number;
  sov: number;
  spend: number;
  ecpm: number;
  viewability: number | null;
  impressions: number;
};

export type DealInsightsDeliveryByAdSizeApi = {
  total_rows: number;
  objects: DealInsightsDeliveryByAdSizeObjectApi[];
  clicks_range: DealInsightsRangeApi;
  ctr_range: DealInsightsRangeApi;
  sov_range: DealInsightsRangeApi;
  spend_range: DealInsightsRangeApi;
  ecpm_range: DealInsightsRangeApi;
  viewability_range: DealInsightsRangeApi;
  impressions_range: DealInsightsRangeApi;
};

export type DealInsightsInventoryDistributionsEntryApi<T> = {
  impressions: T;
  spend: T;
  clicks: T;
};

export const dealInsightsDirectnessKeysApi = {
  TRUE: 'true',
  FALSE: 'false',
} as const;
export type DealInsightsDirectnessKeysApi = ConstValues<typeof dealInsightsDirectnessKeysApi>;

export const dealInsightsChannelKeysApi = {
  SITE: 'site',
  APP: 'app',
} as const;
export type DealInsightsChannelKeysApi = ConstValues<typeof dealInsightsChannelKeysApi>;

export const dealInsightsMediaTypeKeysApi = {
  CTV: 'ctv',
  OLV: 'olv',
  DISPLAY: 'display',
} as const;
export type DealInsightsMediaTypeKeysApi = ConstValues<typeof dealInsightsMediaTypeKeysApi>;

export type DealInsightsDistributionsDirectnessEntryApi = Record<DealInsightsDirectnessKeysApi, number | null>;
export type DealInsightsDistributionsChannelEntryApi = Record<DealInsightsChannelKeysApi, number | null>;
export type DealInsightsDistributionsMediaTypeEntryApi = Record<DealInsightsMediaTypeKeysApi, number | null>;
export type DealInsightsDistributionsDspNameEntryApi = Record<string, number | null>;

export type DealInsightsInventoryDistributionsApi = {
  directness: DealInsightsInventoryDistributionsEntryApi<DealInsightsDistributionsDirectnessEntryApi>;
  channel: DealInsightsInventoryDistributionsEntryApi<DealInsightsDistributionsChannelEntryApi>;
  media_type: DealInsightsInventoryDistributionsEntryApi<DealInsightsDistributionsMediaTypeEntryApi>;
  dsp_name: DealInsightsInventoryDistributionsEntryApi<DealInsightsDistributionsDspNameEntryApi>;
};

export type DealInsightsGeolocationMetricsApi = {
  spend: Record<string, number>;
  impressions: Record<string, number>;
  clicks: Record<string, number>;
};

export type DealInsightsGeolocationApi = {
  country: DealInsightsGeolocationMetricsApi;
  us_state: DealInsightsGeolocationMetricsApi;
};

export type DealInsightsDeliveryByAdDurationApi = {
  impressions_range: DealInsightsRangeApi;
  spend_range: DealInsightsRangeApi;
  clicks_range: DealInsightsRangeApi;
  ctr_range: DealInsightsRangeApi;
  ecpm_range: DealInsightsRangeApi;
  sov_range: DealInsightsRangeApi;
  total_rows: number;
  objects: DealInsightsDeliveryByAdDurationObjectApi[];
};

export type DealInsightsDeliveryByAdDurationObjectApi = {
  ad_duration: string;
  impressions: number | null;
  spend: number | null;
  clicks: number | null;
  ctr: number | null;
  ecpm: number | null;
  sov: number | null;
  viewability: number | null;
};

export type DealInsightsApi = {
  general_stats: DealInsightsGeneralStatsApi;
  spend: DealInsightsSpendApi[];
  bidrate_winrate: DealInsightsBidRateWinRateApi[];
  deal_stats: DealInsightsDealStatsApi;
  delivery_by_domain_bundle: DealInsightsDeliveryByDomainBundleApi;
  audiences: DealInsightsAudiencesApi;
  delivery_by_ad_size: DealInsightsDeliveryByAdSizeApi;
  delivery_by_ad_duration: DealInsightsDeliveryByAdDurationApi;
  inventory_distributions: DealInsightsInventoryDistributionsApi;
  geolocation: DealInsightsGeolocationApi;
};

export type DealInsightsApiResponse = BqJobResponseApi<
  DealInsightsApi & {
    general_stats_with_previous_period?: DealInsightsGeneralStatsApi;
  }
>;

export const dealInsightsFilterOptionKeys = {
  START_DATE: 'startDate',
  END_DATE: 'endDate',
  ACCOUNT: 'account',
  DEAL: 'deal',
  BUYER_NAME: 'buyerName',
  DSP_NAME: 'dspName',
  PUB_NAME: 'pubName',
  TYPE_NAME: 'typeName',
  DOMAIN_BUNDLE: 'domainBundle',
  MEDIA_SUBTYPE: 'mediaSubtype',
  CHANNEL: 'channel',
  ADSIZE: 'adsize',
  AD_DURATION: 'adDuration',
} as const;
export type DealInsightsFilterOptionKeys = ConstValues<typeof dealInsightsFilterOptionKeys>;

export type DealInsightsDealNameOrIdOptions = {
  [dealInsightsFilterOptionKeys.DEAL]: DealInsightsDealEntryApi[];
};

export type DealInsightsMoreFiltersOptions = {
  [dealInsightsFilterOptionKeys.BUYER_NAME]: DealInsightsFilterNullableEntryApi[];
  [dealInsightsFilterOptionKeys.DSP_NAME]: DealInsightsFilterEntryApi[];
  [dealInsightsFilterOptionKeys.PUB_NAME]: DealInsightsFilterEntryApi[];
  [dealInsightsFilterOptionKeys.TYPE_NAME]: DealInsightsFilterEntryApi[];
  [dealInsightsFilterOptionKeys.DOMAIN_BUNDLE]: DealInsightsFilterEntryApi[];
  [dealInsightsFilterOptionKeys.MEDIA_SUBTYPE]: DealInsightsFilterEntryApi[];
  [dealInsightsFilterOptionKeys.CHANNEL]: DealInsightsFilterEntryApi[];
  [dealInsightsFilterOptionKeys.ADSIZE]: DealInsightsFilterEntryApi[];
  [dealInsightsFilterOptionKeys.AD_DURATION]: DealInsightsFilterEntryApi[];
};

export type DealInsightsFilterOptions = DealInsightsDealNameOrIdOptions & DealInsightsMoreFiltersOptions;

export type DealInsightsCacheKeys = keyof DealInsightsChartInputApi;
export type DealInsightsCacheData =
  | DealInsightsGeneralStatsApi
  | DealInsightsSpendApi[]
  | DealInsightsDealStatsApi
  | DealInsightsDeliveryByDomainBundleApi
  | DealInsightsAudiencesApi
  | DealInsightsDeliveryByAdSizeApi
  | DealInsightsInventoryDistributionsApi
  | DealInsightsGeolocationApi;

export type DealInsightsCache = Record<
  Partial<DealInsightsCacheKeys>,
  //string key represents params string without unrelated chart inputs
  Record<string, { timestamp: number; data: DealInsightsCacheData }>
>;
